import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line
} from 'recharts';
import Sidebar from '../Components/Sidebar/Sidebar';
import axios from 'axios';
import verificandoToken from '../../FuncoesGerais/VerificarToken';
import verificandoApp from '../../FuncoesGerais/verificaApp';
import filtrarUsuarioIA from '../../FuncoesGerais/filtrarUsuarioIA';
import { ensureProtocol } from '../../config/backend';
import { useTiposChamado } from '../../hooks/useTiposChamado';
import { fetchStatusChamado } from '../../api/statusChamado';
import useTheme from '../../hooks/useTheme';
import './dashboard.css';

const CHART_GRID_PROPS = { strokeDasharray: '3 3', stroke: 'var(--mac-border)' };
const CHART_TICK = { fontSize: 11, fill: 'var(--mac-text-secondary)' };
const CHART_TICK_SM = { fontSize: 10, fill: 'var(--mac-text-secondary)' };
const CHART_TOOLTIP_STYLE = {
    zIndex: 9999,
    borderRadius: 8,
    border: '0.5px solid var(--mac-border)',
    boxShadow: 'var(--mac-shadow-md)',
    background: 'var(--mac-bg-popover)',
    color: 'var(--mac-text-primary)',
};

const DASHBOARD_EQUIPE_STORAGE_KEY = 'tacoma_dashboard_equipe';

function getDatasPadrao() {
    const fim = new Date();
    const ini = new Date();
    ini.setDate(ini.getDate() - 30);
    const fmt = (d) => {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${day}/${month}/${d.getFullYear()}`;
    };
    return { datInic: fmt(ini), datFina: fmt(fim) };
}

function Dashboard() {
    const { getNome } = useTiposChamado();
    const { isDark } = useTheme();
    const pieSliceStroke = isDark ? '#2C2C2E' : '#FFFFFF';
    const pieLabelLineStroke = isDark ? 'rgba(255,255,255,0.35)' : '#64748b';
    const { datInic: defaultIni, datFina: defaultFim } = getDatasPadrao();
    const [datInic, setDatIni] = useState(defaultIni);
    const [datFina, setdatFin] = useState(defaultFim);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState('');
    const [dadosGeral, setDadosGeral] = useState([]);
    const [dadosTipo, setDadosTipo] = useState([]);
    const [dadosColab, setDadosColab] = useState([]);
    const [totais, setTotais] = useState({ fechadas: 0 });
    const [colunasStatus, setColunasStatus] = useState([]);
    const [indicadores, setIndicadores] = useState({
        chamados_no_sla: 0,
        chamados_fora_sla: 0,
        chamados_sem_prazo: 0,
        total_fechados: 0,
        percentual_sla: 100,
        tempo_medio_texto: '-',
        lista_fora_sla: []
    });
    const [listaAbertosForaSla, setListaAbertosForaSla] = useState([]);
    const [equipeVisualizacao, setEquipeVisualizacao] = useState({ equipe_ids: null, usuarios: [] });
    const [equipeSelecionada, setEquipeSelecionada] = useState('');
    const [abaAtiva, setAbaAtiva] = useState('geral');
    const [dadosSistema, setDadosSistema] = useState([]);
    const [dadosSolicitante, setDadosSolicitante] = useState([]);
    const history = useHistory();

    const verificar = async () => {
        const resultado = await verificandoApp('ATENDENTE');
        if (!resultado) history.push('/');
    };

    function aplicarMascaraData(valor) {
        if (!valor) return '';
        const numeros = valor.replace(/\D/g, '').slice(0, 8);
        if (numeros.length <= 2) return numeros;
        if (numeros.length <= 4) return `${numeros.slice(0, 2)}/${numeros.slice(2)}`;
        return `${numeros.slice(0, 2)}/${numeros.slice(2, 4)}/${numeros.slice(4)}`;
    }

    function parseDataBR(str) {
        if (!str || typeof str !== 'string') return null;
        const parts = str.trim().split(/\D/).filter(Boolean);
        if (parts.length !== 3) return null;
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null;
        const d = new Date(year, month, day);
        if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
        return d;
    }

    function formatarDataBR(date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}/${month}/${date.getFullYear()}`;
    }

    const anteriorMes = () => {
        const dIni = parseDataBR(datInic);
        const dFin = parseDataBR(datFina);
        if (!dIni || !dFin) return;
        dIni.setMonth(dIni.getMonth() - 1);
        dFin.setMonth(dFin.getMonth() - 1);
        const novaIni = formatarDataBR(dIni);
        const novaFin = formatarDataBR(dFin);
        setDatIni(novaIni);
        setdatFin(novaFin);
        buscarDados(novaIni, novaFin);
    };

    const proximoMes = () => {
        const dIni = parseDataBR(datInic);
        const dFin = parseDataBR(datFina);
        if (!dIni || !dFin) return;
        dIni.setMonth(dIni.getMonth() + 1);
        dFin.setMonth(dFin.getMonth() + 1);
        const novaIni = formatarDataBR(dIni);
        const novaFin = formatarDataBR(dFin);
        setDatIni(novaIni);
        setdatFin(novaFin);
        buscarDados(novaIni, novaFin);
    };

    const buscarDados = async (overrideDatIni, overrideDatFin) => {
        verificar();
        const datIni = (overrideDatIni != null ? overrideDatIni : (datInic || '')).trim();
        const datFin = (overrideDatFin != null ? overrideDatFin : (datFina || '')).trim();
        if (!datIni || !datFin) {
            setErro('Informe a data inicial e a data final.');
            return;
        }
        setErro('');
        setLoading(true);
        const soEquipe = equipeVisualizacao.usuarios && equipeVisualizacao.usuarios.length <= 1;
        const rawEquipe = soEquipe ? (equipeVisualizacao.equipe_ids || '') : (equipeSelecionada !== '' ? equipeSelecionada : (equipeVisualizacao.equipe_ids != null && equipeVisualizacao.equipe_ids !== undefined ? equipeVisualizacao.equipe_ids : (localStorage.getItem('equpe') || localStorage.getItem('equipe') || '')));
        const equipe = (rawEquipe == null || rawEquipe === undefined || String(rawEquipe).trim() === '' || String(rawEquipe) === 'undefined') ? '' : String(rawEquipe).trim();

        const datIniFull = datIni + ' 00:00:00';
        const datFinFull = datFin + ' 23:59:59';
        const baseUrl = ensureProtocol(localStorage.getItem('ipBackend2') || '');
        const token = localStorage.getItem('token');
        const headers = { Authorization: `${token}` };
        const paramsComPeriodo = { datIni: datIniFull, datFin: datFinFull, equipe };

        try {
            verificandoToken();
            const paramsEquipe = { equipe };
            const [resGeral, resTipo, resColab, resIndicadores, resSistema, resSolicitante, resAbertosForaSla] = await Promise.all([
                axios.get(baseUrl + '/satis/relatorioGeral', { headers, params: paramsComPeriodo }),
                axios.get(baseUrl + '/satis/relatorioTipo', { headers, params: paramsComPeriodo }),
                axios.get(baseUrl + '/satis/relatorioColab', { headers, params: paramsComPeriodo }),
                axios.get(baseUrl + '/satis/indicadores', { headers, params: paramsComPeriodo }),
                axios.get(baseUrl + '/satis/relatorioSistema', { headers, params: paramsComPeriodo }),
                axios.get(baseUrl + '/satis/relatorioSolicitante', { headers, params: paramsComPeriodo }),
                axios.get(baseUrl + '/satis/abertos-fora-sla', { headers, params: paramsEquipe })
            ]);

            const toKey = (cod) => 'S_' + String(cod ?? '').replace(/\W/g, '_');

            const statusListCadastro = await fetchStatusChamado(false);
            const colunasFromCadastro = (statusListCadastro || []).map((s) => ({ codigo_legado: s.codigo_legado, nome: s.nome || String(s.codigo_legado) }));

            const rawG = resGeral.data;
            const rowsG = Array.isArray(rawG) ? rawG : (rawG?.rows || rawG?.data || []);
            const colsG = (rawG?.colunasStatus && rawG.colunasStatus.length > 0) ? rawG.colunasStatus : colunasFromCadastro;

            const geral = rowsG.map((r) => {
                const obj = { dia: r.DIA ?? r.dia ?? '', fechadas: r.FECHADAS ?? r.fechadas ?? 0 };
                colsG.forEach((st) => { obj[toKey(st.codigo_legado)] = r[toKey(st.codigo_legado)] ?? 0; });
                return obj;
            });
            setDadosGeral(geral);

            const rawC = resColab.data;
            const rowsC = Array.isArray(rawC) ? rawC : (rawC?.rows || rawC?.data || []);
            const colsC = (rawC?.colunasStatus && rawC.colunasStatus.length > 0) ? rawC.colunasStatus : colunasFromCadastro;

            let tot = { fechadas: 0 };
            colsC.forEach((st) => { tot[toKey(st.codigo_legado)] = 0; });
            rowsC.forEach((r) => {
                tot.fechadas += r.FECHADAS ?? r.fechadas ?? 0;
                colsC.forEach((st) => { tot[toKey(st.codigo_legado)] += r[toKey(st.codigo_legado)] ?? 0; });
            });

            const totalFechadosInd = resIndicadores?.data?.total_fechados ?? 0;
            if (totalFechadosInd > 0 && tot.fechadas === 0) {
                tot.fechadas = totalFechadosInd;
            }
            setTotais(tot);
            setColunasStatus(colsC.length > 0 ? colsC : colsG);

            const rawT = resTipo.data;
            const rowsT = Array.isArray(rawT) ? rawT : (rawT?.rows || rawT?.data || []);
            const colsT = (rawT?.colunasStatus && rawT.colunasStatus.length > 0) ? rawT.colunasStatus : colunasFromCadastro;

            const tipo = rowsT.map((r) => {
                const nome = r.tipo_nome ?? getNome(r.TIPO ?? r.tipo ?? r.SATTIP ?? r.satTip);
                let total = r.FECHADAS ?? r.fechadas ?? 0;
                colsT.forEach((st) => { total += r[toKey(st.codigo_legado)] ?? 0; });
                return { name: nome || 'Tipo', value: total, id: r.ID ?? r.id };
            }).filter((d) => d.value > 0);
            setDadosTipo(tipo);

            const colab = (rowsC || [])
                .filter((r) => String(r.CODIGO ?? r.codigo ?? '').trim() !== '')
                .map((r) => {
                    const obj = { name: r.COLABORADOR ?? r.colaborador ?? 'Sem nome', fechadas: r.FECHADAS ?? r.fechadas ?? 0 };
                    colsC.forEach((st) => { obj[toKey(st.codigo_legado)] = r[toKey(st.codigo_legado)] ?? 0; });
                    obj.total = colsC.reduce((acc, st) => acc + (obj[toKey(st.codigo_legado)] ?? 0), 0) + obj.fechadas;
                    return obj;
                })
                .sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
            setDadosColab(colab);

            const ind = resIndicadores?.data || {};
            setIndicadores({
                chamados_no_sla: ind.chamados_no_sla ?? 0,
                chamados_fora_sla: ind.chamados_fora_sla ?? 0,
                chamados_sem_prazo: ind.chamados_sem_prazo ?? 0,
                total_fechados: ind.total_fechados ?? 0,
                percentual_sla: ind.percentual_sla ?? 100,
                tempo_medio_texto: ind.tempo_medio_texto ?? '-',
                lista_fora_sla: Array.isArray(ind.lista_fora_sla) ? ind.lista_fora_sla : []
            });
            const rawSis = resSistema?.data;
            const arrSis = Array.isArray(rawSis) ? rawSis : (rawSis?.rows || rawSis?.data || []);
            setDadosSistema((arrSis || []).map((r) => ({ name: r.sistema_nome ?? r.SISTEMA_NOME ?? r.sistema ?? 'Sem sistema', total: Number(r.total ?? r.TOTAL ?? r.quantidade) || 0 })));
            const rawSol = resSolicitante?.data;
            const arrSol = Array.isArray(rawSol) ? rawSol : (rawSol?.rows || rawSol?.data || []);
            setDadosSolicitante((arrSol || []).map((r) => ({ name: r.solicitante_nome ?? r.SOLICITANTE_NOME ?? r.solicitante ?? 'Solicitante', total: Number(r.total ?? r.TOTAL ?? r.quantidade) || 0 })));
            setListaAbertosForaSla(Array.isArray(resAbertosForaSla?.data) ? resAbertosForaSla.data : []);
        } catch (err) {
            console.error('Erro ao buscar dados do dashboard:', err);
            setErro(err?.response?.status === 404 ? 'Rota do relatório não encontrada. Verifique o backend.' : 'Erro ao carregar dados. Tente novamente.');
            setDadosGeral([]);
            setDadosTipo([]);
            setDadosColab([]);
            setColunasStatus([]);
            setTotais({ fechadas: 0 });
            setIndicadores({ chamados_no_sla: 0, chamados_fora_sla: 0, chamados_sem_prazo: 0, total_fechados: 0, percentual_sla: 100, tempo_medio_texto: '-', lista_fora_sla: [] });
            setListaAbertosForaSla([]);
            setDadosSistema([]);
            setDadosSolicitante([]);
        } finally {
            setLoading(false);
        }
    };

    const carregarEquipeVisualizacao = async () => {
        const baseUrl = ensureProtocol(localStorage.getItem('ipBackend2') || '');
        const token = localStorage.getItem('token');
        if (!baseUrl || !token) return;
        try {
            const res = await axios.get(baseUrl + '/usuarios/equipe-visualizacao', { headers: { Authorization: token } });
            const data = res.data || {};
            const usuarios = filtrarUsuarioIA(Array.isArray(data.usuarios) ? data.usuarios : [], 'nome');
            const equipeIds = data.equipe_ids != null ? data.equipe_ids : '';
            setEquipeVisualizacao({ equipe_ids: equipeIds, usuarios });

            const stored = sessionStorage.getItem(DASHBOARD_EQUIPE_STORAGE_KEY);
            if (stored != null && stored !== '') {
                const idsValidos = usuarios.map((u) => String(u.id ?? u.USUSEQ ?? ''));
                if (idsValidos.includes(String(stored))) {
                    setEquipeSelecionada(stored);
                }
            }
        } catch (e) {
            console.error('Erro ao carregar equipe de visualização:', e);
        }
    };

    useEffect(() => {
        verificar();
        carregarEquipeVisualizacao();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (equipeVisualizacao.equipe_ids !== null) {
            buscarDados();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [equipeVisualizacao.equipe_ids, equipeSelecionada]);

    const coresStatus = ['#2563eb', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#22c55e'];
    const coresPie = ['#2563eb', '#059669', '#d97706', '#7c3aed', '#dc2626', '#0891b2', '#be185d', '#4f46e5', '#0d9488', '#ca8a04'];

    const toKey = (cod) => 'S_' + String(cod ?? '').replace(/\W/g, '_');

    const dadosTipoOrdenados = [...dadosTipo].sort((a, b) => (b.value || 0) - (a.value || 0));
    const dadosTipoPie = dadosTipoOrdenados.map((d, i) => ({ ...d, cor: coresPie[i % coresPie.length] }));

    const totalTipo = dadosTipoPie.reduce((acc, d) => acc + (d.value || 0), 0);

    const dadosStatus = [
        ...colunasStatus.map((st, i) => ({ name: st.nome || String(st.codigo_legado), value: totais[toKey(st.codigo_legado)] ?? 0, cor: coresStatus[i % coresStatus.length] })),
        { name: 'Concluídas', value: totais.fechadas ?? 0, cor: coresStatus[6] }
    ].filter((d) => d.value > 0);

    const totalAbertasAndamento = colunasStatus.reduce((acc, st) => acc + (totais[toKey(st.codigo_legado)] ?? 0), 0);

    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content dashboard-main">
                <div className="container-fluid dashboard-container">
                    <div className="page-header dashboard-header">
                        <h1>Dashboard — Chamados e Atendimentos</h1>
                        <div className="dashboard-toolbar">
                            <div className="dashboard-filtro-equipe">
                                <label className="dashboard-filtro-label">Visualizar dados de:</label>
                                {(equipeVisualizacao.usuarios || []).length <= 1 ? (
                                    <span className="dashboard-filtro-equipe-static" title="Você visualiza apenas seus próprios dados.">
                                        Meus dados
                                    </span>
                                ) : (
                                    <select
                                        value={equipeSelecionada}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setEquipeSelecionada(val);
                                            try {
                                                if (val) sessionStorage.setItem(DASHBOARD_EQUIPE_STORAGE_KEY, val);
                                                else sessionStorage.removeItem(DASHBOARD_EQUIPE_STORAGE_KEY);
                                            } catch (_) {}
                                        }}
                                        className="form-control form-control-sm dashboard-select-equipe"
                                        disabled={loading}
                                        aria-label="Escopo de visualização"
                                    >
                                        <option value="">Todos da minha equipe</option>
                                        {(equipeVisualizacao.usuarios || []).map((u) => (
                                            <option key={u.id} value={String(u.id)}>{u.nome || 'Usuário ' + u.id}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="input-group dashboard-dates">
                                <div className="input-group-prepend">
                                    <button type="button" className="btn btn-outline-secondary dashboard-date-arrow" onClick={anteriorMes} disabled={loading} title="Mês anterior" aria-label="Mês anterior">
                                        <i className="fas fa-chevron-left" />
                                    </button>
                                </div>
                                <input
                                    value={datInic}
                                    onChange={(e) => setDatIni(aplicarMascaraData(e.target.value))}
                                    type="text"
                                    className="form-control"
                                    placeholder="Data Inicial"
                                />
                                <input
                                    value={datFina}
                                    onChange={(e) => setdatFin(aplicarMascaraData(e.target.value))}
                                    type="text"
                                    className="form-control"
                                    placeholder="Data Final"
                                />
                                <div className="input-group-append">
                                    <button type="button" className="btn btn-outline-secondary dashboard-date-arrow" onClick={proximoMes} disabled={loading} title="Próximo mês" aria-label="Próximo mês">
                                        <i className="fas fa-chevron-right" />
                                    </button>
                                </div>
                                <button onClick={() => buscarDados()} className="btn btn-primary" type="button" disabled={loading}>
                                    {loading ? 'Carregando...' : 'Atualizar'}
                                </button>
                            </div>
                        </div>
                    </div>
                    {erro && <div className="alert alert-warning dashboard-alert">{erro}</div>}

                    <div className="dashboard-principal-wrapper">
                        <div className="dashboard-tabs" role="tablist">
                            <button type="button" role="tab" aria-selected={abaAtiva === 'geral'} className={`dashboard-tab ${abaAtiva === 'geral' ? 'active' : ''}`} onClick={() => setAbaAtiva('geral')}>Visão geral</button>
                            <button type="button" role="tab" aria-selected={abaAtiva === 'atendimento'} className={`dashboard-tab ${abaAtiva === 'atendimento' ? 'active' : ''}`} onClick={() => setAbaAtiva('atendimento')}>Atendimento por sistema e solicitante</button>
                        </div>

                    {abaAtiva === 'atendimento' && (
                        <div className="dashboard-aba-atendimento dashboard-aba-conteudo">
                            <div className="dashboard-charts">
                                <div className="dashboard-chart-box dashboard-chart-box-wide">
                                    <h3>Chamados por sistema</h3>
                                    {dadosSistema.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={Math.max(300, dadosSistema.length * 40)}>
                                            <BarChart data={dadosSistema} layout="vertical" margin={{ top: 8, right: 24, left: 180, bottom: 8 }}>
                                                <CartesianGrid {...CHART_GRID_PROPS} />
                                                <XAxis type="number" tick={CHART_TICK} />
                                                <YAxis type="category" dataKey="name" width={176} tick={CHART_TICK} />
                                                <Tooltip formatter={(v) => [v, 'Chamados']} />
                                                <Bar dataKey="total" name="Chamados" fill="#2563eb" radius={[0, 4, 4, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <p className="dashboard-empty">Nenhum dado no período.</p>
                                    )}
                                </div>
                                <div className="dashboard-chart-box dashboard-chart-box-wide">
                                    <h3>Chamados por usuário de abertura (solicitante)</h3>
                                    {dadosSolicitante.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={Math.max(300, dadosSolicitante.length * 40)}>
                                            <BarChart data={dadosSolicitante} layout="vertical" margin={{ top: 8, right: 24, left: 180, bottom: 8 }}>
                                                <CartesianGrid {...CHART_GRID_PROPS} />
                                                <XAxis type="number" tick={CHART_TICK} />
                                                <YAxis type="category" dataKey="name" width={176} tick={CHART_TICK} />
                                                <Tooltip formatter={(v) => [v, 'Chamados']} />
                                                <Bar dataKey="total" name="Chamados" fill="#059669" radius={[0, 4, 4, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <p className="dashboard-empty">Nenhum dado no período.</p>
                                    )}
                                </div>
                            </div>
                            <div className="dashboard-aba-resumo">
                                <p className="dashboard-aba-desc">Chamados considerados pela <strong>data de abertura</strong> no período, filtrados pela equipe selecionada.</p>
                            </div>
                        </div>
                    )}

                    {abaAtiva === 'geral' && (
                    <div className="dashboard-aba-conteudo">
                    <div className="dashboard-indicadores-row">
                        <div className="dashboard-card card-sla-ok">
                            <span className="card-num">{indicadores.chamados_no_sla}</span>
                            <span className="card-label">No SLA</span>
                        </div>
                        <div className="dashboard-card card-sla-fora">
                            <span className="card-num">{indicadores.chamados_fora_sla}</span>
                            <span className="card-label">Fora do SLA</span>
                        </div>
                        <div className="dashboard-card card-sla-pct">
                            <span className="card-num">{indicadores.percentual_sla}%</span>
                            <span className="card-label">% no prazo</span>
                        </div>
                        <div className="dashboard-card card-tempo-medio">
                            <span className="card-num">{indicadores.tempo_medio_texto}</span>
                            <span className="card-label">Tempo médio atend.</span>
                        </div>
                        <div className="dashboard-card card-fechados-periodo">
                            <span className="card-num">{indicadores.total_fechados}</span>
                            <span className="card-label">Fechados no período</span>
                        </div>
                    </div>

                    <div className="dashboard-cards-row">
                        <div className="dashboard-cards">
                            {colunasStatus
                                .filter((st) => (totais[toKey(st.codigo_legado)] ?? 0) > 0)
                                .map((st, i) => (
                                    <div key={st.codigo_legado} className={`dashboard-card card-status-${i}`}>
                                        <span className="card-num">{totais[toKey(st.codigo_legado)] ?? 0}</span>
                                        <span className="card-label">{st.nome || String(st.codigo_legado)}</span>
                                    </div>
                                ))}
                        </div>
                        <div className="dashboard-totalizador-wrapper">
                            <div className="dashboard-totalizador">
                                <span className="card-num">{totalAbertasAndamento}</span>
                                <span className="card-label">Total</span>
                            </div>
                            <div className="dashboard-card card-fechadas">
                                <span className="card-num">{totais.fechadas ?? 0}</span>
                                <span className="card-label">Concluídas</span>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-charts">
                        <div className="dashboard-chart-box">
                            <h3>Chamados por status</h3>
                            {dadosStatus.length > 0 ? (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={dadosStatus} margin={{ top: 12, right: 12, left: 12, bottom: 24 }}>
                                        <CartesianGrid {...CHART_GRID_PROPS} />
                                        <XAxis dataKey="name" tick={CHART_TICK} />
                                        <YAxis tick={CHART_TICK} />
                                        <Tooltip />
                                        <Bar dataKey="value" name="Quantidade" radius={[4, 4, 0, 0]}>
                                            {dadosStatus.map((entry, i) => <Cell key={i} fill={entry.cor} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="dashboard-empty">Nenhum dado no período.</p>
                            )}
                        </div>

                        <div className="dashboard-chart-box dashboard-chart-box-pie">
                            <h3>Chamados por tipo</h3>
                            {dadosTipoPie.length > 0 ? (
                                <ResponsiveContainer width="100%" height={320}>
                                    <PieChart margin={{ top: 12, right: 12, left: 12, bottom: 12 }}>
                                        <Pie
                                            data={dadosTipoPie}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="45%"
                                            outerRadius={100}
                                            innerRadius={0}
                                            paddingAngle={2}
                                            label={({ name, value }) => `${name} (${value})`}
                                            labelLine={{ strokeWidth: 1.5, stroke: pieLabelLineStroke }}
                                        >
                                            {dadosTipoPie.map((entry, i) => <Cell key={i} fill={entry.cor} stroke={pieSliceStroke} strokeWidth={1.5} />)}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value, name) => {
                                                const pct = totalTipo > 0 ? Math.round((value / totalTipo) * 100) : 0;
                                                return [`${value} chamados (${pct}%)`, name];
                                            }}
                                            contentStyle={CHART_TOOLTIP_STYLE}
                                            itemStyle={{ padding: '4px 0' }}
                                        />
                                        <Legend
                                            layout="horizontal"
                                            align="center"
                                            verticalAlign="bottom"
                                            wrapperStyle={{ paddingTop: 12 }}
                                            formatter={(value, entry) => <span className="dashboard-pie-legend-item">{value} <small>({entry.payload?.value ?? 0})</small></span>}
                                            iconType="square"
                                            iconSize={10}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="dashboard-empty">Nenhum dado no período.</p>
                            )}
                        </div>

                        <div className="dashboard-chart-box dashboard-chart-box-wide">
                            <h3>{(equipeVisualizacao.usuarios || []).length <= 1 || equipeSelecionada !== '' ? 'Performance' : 'Equipe por status (todos os colaboradores)'}</h3>
                            {dadosColab.length > 0 ? (
                                <ResponsiveContainer width="100%" height={Math.max(280, dadosColab.length * 36)}>
                                    <BarChart data={dadosColab} layout="vertical" margin={{ top: 8, right: 24, left: 120, bottom: 8 }}>
                                        <CartesianGrid {...CHART_GRID_PROPS} />
                                        <XAxis type="number" tick={CHART_TICK} />
                                        <YAxis type="category" dataKey="name" width={116} tick={CHART_TICK} />
                                        <Tooltip
                                            wrapperStyle={{ zIndex: 9999 }}
                                            contentStyle={CHART_TOOLTIP_STYLE}
                                            content={({ active, payload, label }) => {
                                                if (!active || !payload || !payload.length) return null;
                                                const somenteComValor = payload.filter((entry) => (entry.value ?? 0) > 0);
                                                if (somenteComValor.length === 0) return null;
                                                return (
                                                    <div className="dashboard-equipe-tooltip">
                                                        <div className="dashboard-equipe-tooltip-title">{label}</div>
                                                        <ul className="dashboard-equipe-tooltip-list">
                                                            {somenteComValor.map((entry, i) => (
                                                                <li key={i} style={{ color: entry.color }}>
                                                                    {entry.name}: {entry.value}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Legend />
                                        {colunasStatus.map((st, i) => (
                                            <Bar key={st.codigo_legado} dataKey={toKey(st.codigo_legado)} name={st.nome || String(st.codigo_legado)} fill={coresStatus[i % coresStatus.length]} stackId="a" radius={[0, 0, 0, 0]} />
                                        ))}
                                        <Bar dataKey="fechadas" name="Concluídas" fill="#22c55e" stackId="a" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="dashboard-empty">Nenhum dado no período.</p>
                            )}
                        </div>

                        {dadosGeral.length > 0 && (
                            <div className="dashboard-chart-box dashboard-chart-box-wide">
                                <h3>{(equipeVisualizacao.usuarios || []).length <= 1 || equipeSelecionada !== '' ? 'Evolução no período (individual)' : 'Evolução no período (equipe)'}</h3>
                                <ResponsiveContainer width="100%" height={260}>
                                    <LineChart data={dadosGeral} margin={{ top: 8, right: 16, left: 8, bottom: 24 }}>
                                        <CartesianGrid {...CHART_GRID_PROPS} />
                                        <XAxis dataKey="dia" tick={CHART_TICK_SM} />
                                        <YAxis tick={CHART_TICK} />
                                        <Tooltip />
                                        <Legend />
                                        {colunasStatus.map((st, i) => (
                                            <Line key={st.codigo_legado} type="monotone" dataKey={toKey(st.codigo_legado)} name={st.nome || String(st.codigo_legado)} stroke={coresStatus[i % coresStatus.length]} strokeWidth={2} dot={{ r: 3 }} />
                                        ))}
                                        <Line type="monotone" dataKey="fechadas" name="Concluídas" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    <div className="dashboard-fora-sla-section dashboard-abertos-fora-sla-section">
                        <h3>Chamados em aberto com SLA estourada</h3>
                        {listaAbertosForaSla && listaAbertosForaSla.length > 0 ? (
                            <div className="dashboard-table-wrap">
                                <table className="table table-modern dashboard-fora-sla-table">
                                    <thead>
                                        <tr>
                                            <th>Chamado</th>
                                            <th>Tipo</th>
                                            <th>Prazo (h)</th>
                                            <th>Tempo decorrido</th>
                                            <th>Abertura</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {listaAbertosForaSla.map((item) => (
                                            <tr key={item.SATNUM}>
                                                <td>
                                                    <Link to={`/historicoSati/${item.SATNUM}`} className="dashboard-link-chamado">#{item.SATNUM}</Link>
                                                </td>
                                                <td>{item.tipo_nome}</td>
                                                <td>{item.prazo_em_horas}</td>
                                                <td><strong>{item.tempo_texto}</strong></td>
                                                <td>{item.data_abertura}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="dashboard-empty">Nenhum chamado em aberto com SLA estourada.</p>
                        )}
                    </div>

                    <div className="dashboard-fora-sla-section">
                        <h3>Chamados fora do SLA (fechados no período)</h3>
                        {indicadores.lista_fora_sla && indicadores.lista_fora_sla.length > 0 ? (
                            <div className="dashboard-table-wrap">
                                <table className="table table-modern dashboard-fora-sla-table">
                                    <thead>
                                        <tr>
                                            <th>Chamado</th>
                                            <th>Tipo</th>
                                            <th>Prazo (h)</th>
                                            <th>Tempo real</th>
                                            <th>Abertura</th>
                                            <th>Fechamento</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {indicadores.lista_fora_sla.map((item) => (
                                            <tr key={item.SATNUM}>
                                                <td>
                                                    <Link to={`/historicoSatiFechada/${item.SATNUM}`} className="dashboard-link-chamado">#{item.SATNUM}</Link>
                                                </td>
                                                <td>{item.tipo_nome}</td>
                                                <td>{item.prazo_em_horas}</td>
                                                <td><strong>{item.tempo_texto}</strong></td>
                                                <td>{item.data_abertura}</td>
                                                <td>{item.data_fechamento}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="dashboard-empty">Nenhum chamado fora do SLA no período.</p>
                        )}
                    </div>
                    </div>
                    )}
                    </div>
                </div>
            </main>
        </div>
    );
}

export default Dashboard;
